import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Table, Button, Space, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import config from "../../config";
import SelectUsersExcelButton from "../Forms/SelectUsersExcelButton";

const SelectUsersMiniTable = ({
  value = [],
  onChange,
  apiUrl = "/api/users/getuserslist",
  pageSize = 5,
  rowKey = "userID",
  rtl = true,
  className = "",
}) => {
  const [filters, setFilters] = useState({
    lastname: "",
    username: "",
    personalId: "",
    phoneNumber: "",
    firstname: "",
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5 });

  const [users, setUsers] = useState([]);
  const initialUsersRef = useRef([]);
  const initialTotalCountRef = useRef([]);

  const selectedUsers = value || [];
  const selectedRowKeys = useMemo(
    () =>
      selectedUsers.filter(u => u).map((u) =>
        typeof rowKey === "function" ? rowKey(u) : u[rowKey]
      ),
    [selectedUsers, rowKey]
  );
  const dedupeByKey = (arr, keyName) => {
    const map = new Map();
    arr.forEach((it) => map.set(it[keyName], it));
    return Array.from(map.values());
  };

  const putSelectedOnTop = (usersList = []) => {
    const keyName = typeof rowKey === "function" ? "userID" : rowKey;
    const selIdSet = new Set(
      selectedUsers.map((u) =>
        typeof rowKey === "function" ? rowKey(u) : u[rowKey]
      )
    );
    const selected = [];
    const remaining = [];
    usersList.forEach((u) =>
      (selIdSet.has(u[keyName]) ? selected : remaining).push(u)
    );
    const orderedSelected = selectedUsers
      .map((s) =>
        usersList.find(
          (u) =>
            (typeof rowKey === "function" ? rowKey(u) : u[rowKey]) ===
            (typeof rowKey === "function" ? rowKey(s) : s[rowKey])
        )
      )
      .filter(Boolean);
    const rest = remaining.filter(
      (r) =>
        !orderedSelected.some(
          (os) =>
            (typeof rowKey === "function" ? rowKey(os) : os[rowKey]) ===
            (typeof rowKey === "function" ? rowKey(r) : r[rowKey])
        )
    );
    return [...orderedSelected, ...rest];
  };

  const fetchUsers = useCallback(
    async (page = 1, size = pageSize) => {
      setLoading(true);
      try {
        
        const filterParts = [];
        if (filters.username) {
          filterParts.push(`username=*${filters.username}/i`);
        }
        if (filters.personalId) {
          filterParts.push(`personalId=*${filters.personalId}/i`);
        }
        if (filters.lastname) {
          filterParts.push(`lastname=*${filters.lastname}/i`);
        }
        if (filters.firstname) {
          filterParts.push(`firstname=*${filters.firstname}/i`);
        }
        if (filters.phoneNumber) {
          filterParts.push(`phoneNumber=*${filters.phoneNumber}/i`);
        }

        const dynamicFilter = filterParts.join(',');

        const params = {
          page,
          pageSize: size,
          filter: dynamicFilter || undefined,
        };
        const response = await axios.get(
          `${config.apiBaseUrl}/api/users/getuserslist`,
          {
            params,
            headers: { HeaderKey: config.HeaderKey },
            withCredentials: true,
          }
        );
        setTotal(response.data.totalCount);
        setUsers(response.data.users);
        if (initialUsersRef.current.length === 0) {
          initialUsersRef.current = response.data.users;
          initialTotalCountRef.current = response.data.totalCount;
       }
      } catch {
        setUsers([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    
    [filters, pageSize] 
  );
  const clearSearchFilters = () => {
    setFilters({ lastname: "", username: "", personalId: "", phoneNumber: "", firstname: "" });
    setUsers(initialUsersRef.current);
    setTotal(initialTotalCountRef.current);
  };
  useEffect(() => {
    fetchUsers(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  const handleSearch = () => {
    
    setPagination(p => ({ ...p, current: 1 }));
    fetchUsers(1, pagination.pageSize);
  }

  const makeFilter = (field, placeholder) => (
    <div style={{ padding: 8 }}>
      <Space.Compact style={{ width: "100%" }}>
        <Input
          placeholder={placeholder}
          allowClear
          onClear={() => {
            setFilters((prevState) => ({
              ...prevState,
              [field]: "",
            }));
            clearSearchFilters();
          }}
          value={filters[field]}
          onChange={(e) =>
            setFilters((p) => ({ ...p, [field]: e.target.value }))
          }
          onPressEnter={handleSearch}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
        />
      </Space.Compact>
    </div>
  );

  const columns = [
    {
      title: "کد ملی",
      className: "font-custom",
      dataIndex: "personalId",
      key: "personalId",
      align: "center",
      filterDropdown: () => makeFilter("personalId", "جستجوی کدملی"),
    },
    {
      title: "شماره تلفن",
      className: "font-custom",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      align: "center",
      filterDropdown: () => makeFilter("phoneNumber", "جستجوی شماره تلفن"),
    },
    {
      title: "نام کاربری",
      className: "font-custom",
      dataIndex: "username",
      key: "username",
      align: "right",
      filterDropdown: () => makeFilter("username", "جستجوی نام کاربری"),
    },
    {
      title: "نام خانوادگی",
      className: "font-custom",
      dataIndex: "lastname",
      key: "lastname",
      align: "right",
      filterDropdown: () => makeFilter("lastname", "جستجوی نام خانوادگی"),
    },
  ];
const handleSelectionChange = (newKeys) => {
  
  const stillSelected = selectedUsers.filter((u) => {
    const id = typeof rowKey === "function" ? rowKey(u) : u[rowKey];
    return newKeys.includes(id);
  });

  const newlySelected = users.filter((u) => {
    const id = typeof rowKey === "function" ? rowKey(u) : u[rowKey];
    return newKeys.includes(id) && !stillSelected.some(s => 
       (typeof rowKey === "function" ? rowKey(s) : s[rowKey]) === id
    );
  });

  const finalSelection = [...stillSelected, ...newlySelected];
  
  onChange && onChange(finalSelection.filter(Boolean));
};

  const removeSelectedUser = (id) => {
    const next = selectedUsers.filter(
      (u) => (typeof rowKey === "function" ? rowKey(u) : u[rowKey]) !== id
    );
    onChange && onChange(next);
  };

  return (
    <div
      className={`grid ${
        rtl ? "text-right rtl" : "text-left ltr"
      } grid-cols-1 gap-4 ${className}`}
    >
      <div className="flex items-center my-8">
        <div className="flex-grow border-t border-gray-300" />
        <h2 className="mx-4 text-xl font-bold text-gray-400 whitespace-nowrap font-custom">
          انتخاب کاربران
        </h2>
        <div className="flex-grow border-t border-gray-300" />
      </div>

      <div className="mb-3 flex justify-start">
        <SelectUsersExcelButton
          onSelected={(newUsers) => {
            setUsers(prev => {
              const map = new Map(prev.map(u => [u.userID, u]));
              newUsers.forEach(u => map.set(u.userID, u));
              return Array.from(map.values());
            });
            const map = new Map(selectedUsers.map(u => [u.userID, u]));
            newUsers.forEach(u => map.set(u.userID, u));
            onChange && onChange(Array.from(map.values()));
          }}
        />
      </div>

      <Table
        rowSelection={{ selectedRowKeys, onChange: handleSelectionChange, preserveSelectedRowKeys: true }}
        columns={columns}
        dataSource={users}
        rowKey={rowKey}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({ ...prev, current: page, pageSize }));
            fetchUsers(page, pageSize);
          },
        }}
        loading={loading}
      />

      <h3 className="text-lg font-semibold mb-2 text-right font-custom">
        کاربران انتخاب‌شده
      </h3>
      <Table
        columns={[
          {
            title: "حذف",
            key: "actions",
            className: "font-custom",
            align: "center",
            render: (_, record) => {
              if (!record) return null;
              const id =
                typeof rowKey === "function" ? rowKey(record) : record[rowKey];
              return (
                <Button danger size="small" onClick={() => removeSelectedUser(id)}>
                  حذف
                </Button>
              );
            },
          },
          {
            title: "نام کاربری",
            className: "font-custom",
            dataIndex: "username",
            key: "username",
            align: rtl ? "right" : "left",
          },
          {
            title: "شماره تلفن",
            className: "font-custom",
            dataIndex: "phoneNumber",
            key: "phoneNumber",
            align: "center",
          },
          {
            title: "نام خانوادگی",
            className: "font-custom",
            dataIndex: "lastname",
            key: "lastname",
            align: rtl ? "right" : "left",
          },
          {
            title: "نام",
            className: "font-custom",
            dataIndex: "firstname",
            key: "firstname",
            align: rtl ? "right" : "left",
          },
        ]}
        dataSource={selectedUsers}
        rowKey={rowKey}
        pagination={false}
      />
    </div>
  );
};

export default SelectUsersMiniTable;